import React, { useState, useCallback, useEffect } from 'react'
import cx from 'classnames'
import styles from './Modal.module.scss'
import Portal from '../Portal'
import ModalHeader from './ModalHeader'
import { hideModal, showModal } from './utils'
import useOnClickOutside from '../../../hooks/useOnClickOutside'

export const Modal = ({
  id,
  title,
  children,
  shouldCloseOnOverlayClick,
  shouldCloseOnEsc,
  hasCloseButton,
  className,
  positionBottom,
  onClose,
  themeMode,
  bodyStyle = {},
  modalCoverStyle = {},
  bodyClassName,
  headerClassName,
  ...rest
}) => {
  const [modalData, setModalData] = useState(false)
  const [modal, setModal] = useState()
  const [modalCover, setModalCover] = useState()

  useEffect(() => {
    function handleShow (e) {
      const { id: dispatchedId, data } = e.detail
      if (id === dispatchedId) {
        setModalData(data || {})
      }
    }

    function handleHide (e) {
      const { id: dispatchedId } = e.detail
      if (id === dispatchedId) {
        setModalData(false)
      }
    }

    // eslint-disable-next-line no-undef
    document.addEventListener('showModal', handleShow)
    // eslint-disable-next-line no-undef
    document.addEventListener('hideModal', handleHide)

    return () => {
      // eslint-disable-next-line no-undef
      document.removeEventListener('showModal', handleShow)
      // eslint-disable-next-line no-undef
      document.removeEventListener('hideModal', handleHide)
    }
  }, [id])

  useEffect(() => {
    if (modalCover) {
      modalCover.focus()
    }
  }, [modalCover])

  const closeModal = useCallback(() => {
    if (onClose) {
      onClose()
    }
    hideModal(id)
  }, [id])

  const handleKeyDown = useCallback(
    (event) => {
      if (!shouldCloseOnEsc) {
        return
      }
      if (event.keyCode === 27) {
        closeModal()
      }
    },
    [shouldCloseOnEsc, closeModal]
  )

  useOnClickOutside({ current: modal }, () => {
    if (shouldCloseOnOverlayClick) {
      closeModal()
    }
  })

  if (!modalData) return null

  return (
    <Portal>
      {modalData && (
        <div
          role='dialog'
          tabIndex='-1'
          aria-modal='true'
          style={modalCoverStyle}
          className={cx(`${styles.modalCover} ${styles[themeMode]}`, positionBottom && styles.positionBottom)}
          onKeyDown={handleKeyDown}
          ref={setModalCover}
        >
          <div className={cx(`${styles.modalArea}  ${styles[themeMode]}`, className)} ref={setModal} {...rest}>
            {(title || hasCloseButton) && (
              <ModalHeader headerClassName={headerClassName} themeMode={themeMode} closeModal={closeModal} hasCloseButton={hasCloseButton}>
                {title}
              </ModalHeader>
            )}
            <div style={bodyStyle} className={cx(`${styles.body} ${styles[themeMode]}`, className, bodyClassName)}>
              {typeof children === 'function' ? children({ modalData }) : children}
            </div>
          </div>
        </div>
      )}
    </Portal>
  )
}

Modal.defaultProps = {
  title: undefined,
  children: undefined,
  className: undefined,
  shouldCloseOnOverlayClick: true,
  shouldCloseOnEsc: false,
  hasCloseButton: true,
  positionBottom: false
}

Modal.hide = hideModal
Modal.show = showModal

export default Modal
